/** @jsx jsx */
import { Box, Container, EmptyState, EmptyStateListWhite, Flex, Pagination, Paragraph } from "@eworkit/iris";
import { FC, memo, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { LoadingSpinner } from "@/components/Loading";
import { UserContext } from "@/components/User";
import { ListViewHeader } from "@/components/views";
import { IURLSortConfig } from "@/core/url";
import { fetchContractsList, IContractList, IPageableContractsList } from "@/data/contracts";
import { TPageableFetchFunction, usePageableFetcher } from "@/hooks/http";

import { ContractsList } from "./ContractsList";

export const ContractsView: FC = memo(() => {
  const { t } = useTranslation("session");
  const { contextCompany } = useContext(UserContext);

  const fetchContracts: TPageableFetchFunction<IContractList, IPageableContractsList, {}, IURLSortConfig[]> =
    useCallback(
      (params, options) => {
        return fetchContractsList(
          {
            companyId: contextCompany?.id,
            ...params,
          },
          options,
        );
      },
      [contextCompany],
    );

  const { working, payload, changePage, changePageSize } = usePageableFetcher(fetchContracts);

  const noMatchingContracts = useMemo(() => {
    return !working && !payload?.content.length;
  }, [working, payload?.content]);

  return (
    <Flex flexDirection="column" gap={["xs", null, "sm", "md"]} pb="lg">
      <ListViewHeader header={t("CONTRACTS.LIST.SECTION_HEADER")} count={payload?.totalElements}>
        <Paragraph
          sx={{
            color: "n700",
          }}>
          {t("CONTRACTS.LIST.SECTION_DESCRIPTION")}
        </Paragraph>
      </ListViewHeader>
      {noMatchingContracts ? (
        <Box sx={{ py: 120 }} px="sm">
          <EmptyState
            icon={<EmptyStateListWhite />}
            title={t("CONTRACTS.NO_MATCHING_CONTRACTS")}
            content={t("CONTRACTS.NO_MATCHING_CONTRACTS_DESCRIPTION")}
          />
        </Box>
      ) : (
        <Container>
          <Flex px={["0", null, "sm"]} flexDirection="column" gap="lg">
            {working && (
              <Flex justifyContent="center" sx={{ py: 120 }}>
                <LoadingSpinner size="lg" variant="primary" />
              </Flex>
            )}
            {payload && !working && <ContractsList contracts={payload.content.map(item => item.data)} />}
            {payload && !working && (
              <Pagination
                pagesCount={payload.totalPages}
                activeIndex={payload.number}
                onChangeIndex={changePage}
                pageSize={payload.size}
                onChangePageSize={changePageSize}
                resultsLabel={t("public:COMPONENTS.PAGINATION.RESULTS")}
              />
            )}
          </Flex>
        </Container>
      )}
    </Flex>
  );
});
